'use client';

import { parseAsString, useQueryState } from 'nuqs';
import { TypedSearchInput } from '~/components/primitives/TypedSearchInput';
import Title from '~/components/utils/Title';
import { useI18n } from '~/features/i18n';
import { AnimatePresence, motion } from 'motion/react';
import { useCallback } from 'react';
import { cn } from '~/utils';
import {
    ACTIVITY_SEARCH_MODE_QUERY_PARAMS,
    ACTIVITY_SEARCH_NUQS_DEFAULT_OPTIONS,
    PLP_SEARCH_INPUT_ID,
    PLP_SEARCH_TITLE_ID,
} from './shared';

const INPUT_TEXT_CLASSES =
    'text-[1.75rem] font-extrabold leading-[100%] sm:text-[2rem] md:text-[2.5rem] lg:text-[2.5rem] xl:text-[3.25rem]';

export const SearchTitle: React.FC<{
    defaultTitle: React.ReactNode;
    className?: string;
    style?: React.CSSProperties;
}> = ({ defaultTitle, className, style }) => {
    const i18n = useI18n();
    // Use the shared search options: `scroll: false` stops the page jumping to the top when the
    // title becomes the search input (and on every keystroke), `shallow: true` keeps it client-side,
    // and `throttleMs` debounces the URL writes while typing.
    const [query, setQuery] = useQueryState(
        'query',
        parseAsString.withOptions(ACTIVITY_SEARCH_NUQS_DEFAULT_OPTIONS),
    );
    const [mode] = useQueryState(
        'mode',
        ACTIVITY_SEARCH_MODE_QUERY_PARAMS.mode.withOptions(ACTIVITY_SEARCH_NUQS_DEFAULT_OPTIONS),
    );
    const isAiMode = (mode ?? 'normal') === 'ai';
    // The input only mounts once the title turns into it — on a title click, or when
    // `?query=` arrives from the header's search entry (fresh load or client-side
    // navigation) — so focusing on mount always hands the visitor a cursor. Stable so
    // React calls it on mount only, not on every keystroke.
    const focusOnMount = useCallback((el: HTMLInputElement | null) => el?.focus(), []);

    /* min-h (not fixed h) so wrapped headlines aren't clipped on narrow viewports; keeps similar floor when switching to search */
    const titleBlockMinHeight =
        'min-h-[4rem] sm:min-h-[4rem] md:min-h-[4.5rem] lg:min-h-[5rem] xl:min-h-[5.75rem]';

    return (
        <div
            className={cn(
                'flex w-full min-w-0 max-w-full flex-col items-center',
                /* Bottom-align the headline so min-height reserves space upward (for wraps) without a large gap above the filter bar */
                query == null ? 'justify-end' : 'justify-center',
                titleBlockMinHeight,
            )}
        >
            <AnimatePresence mode="wait" initial={false}>
                {query == null ? (
                    <motion.div
                        key={isAiMode ? 'title-ai' : 'title-default'}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.15 }}
                        className={cn(
                            // In AI mode the sidebar is always open — nudge the hero title down a bit.
                            'flex w-full items-center justify-center transition-transform duration-200',
                            isAiMode && 'translate-y-3 sm:translate-y-4',
                        )}
                    >
                        <Title
                            as="h1"
                            // No id in AI mode: the title does not open the search there.
                            id={isAiMode ? undefined : PLP_SEARCH_TITLE_ID}
                            size="4xl"
                            className={className}
                            style={style}
                            onClick={isAiMode ? undefined : () => setQuery('')}
                        >
                            {isAiMode ? (
                                i18n._('activity.search.ai.heroTitle', undefined, {
                                    message: 'Prompt your search',
                                })
                            ) : (
                                <span className="cursor-text">{defaultTitle}</span>
                            )}
                        </Title>
                    </motion.div>
                ) : (
                    <motion.div
                        key="search"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.15 }}
                        className="flex w-full min-w-0 max-w-full items-center justify-center gap-3 lg:gap-4"
                    >
                        <TypedSearchInput
                            ref={focusOnMount}
                            id={PLP_SEARCH_INPUT_ID}
                            value={query}
                            onChange={e => setQuery(e.currentTarget.value)}
                            placeholder={i18n._('activity.search.placeholder', undefined, {
                                message: 'Search activities',
                            })}
                            onClear={() => setQuery('')}
                            clearLabel={i18n._('activity.search.clear', undefined, {
                                message: 'Clear search',
                            })}
                            textClassName={INPUT_TEXT_CLASSES}
                            iconClassName="size-6 sm:size-7 xl:size-9"
                            className="gap-4 lg:gap-6"
                            style={style}
                        />
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};
